import { c as _c } from "react/compiler-runtime";
import type { Document, DocumentUpload } from '@onefootprint/request-types/dashboard';
import { LinkButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EntityVault } from '@/types/vault';
import type { Rotation } from '@/utils/rotation';
import { getUploadKey } from '../../utils/get-doc-upload-key';
import UploadImageItem from '../upload-image-item';
import UploadTitleCard from '../upload-title-card';
import getTimestampRange from './utils/get-timestamp-range';
export type FailedUploadsProps = {
  document: Document;
  uploads: (DocumentUpload & {
    isLatest: boolean;
  })[];
  vault: EntityVault;
  rotations: Record<string, Rotation>;
  onRotate: (uploadKey: string) => void;
};
const FailedUploads = forwardRef<HTMLDivElement, FailedUploadsProps>((t0, ref) => {
  const $ = _c(43);
  const {
    document,
    uploads,
    vault,
    rotations,
    onRotate
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "fieldset.documents.details"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const [isCollapsed, setIsCollapsed] = useState(true);
  if (uploads.length === 1) {
    let t2;
    if ($[1] !== uploads[0]) {
      t2 = getUploadKey(uploads[0]);
      $[1] = uploads[0];
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    const uploadKey = t2;
    const t3 = rotations[uploadKey] ?? 0;
    let t4;
    if ($[3] !== onRotate || $[4] !== uploadKey) {
      t4 = () => onRotate(uploadKey);
      $[3] = onRotate;
      $[4] = uploadKey;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] !== document || $[7] !== ref || $[8] !== t3 || $[9] !== t4 || $[10] !== uploads[0] || $[11] !== vault) {
      t5 = <UploadImageItem document={document} upload={uploads[0]} vault={vault} ref={ref} rotation={t3} onRotate={t4} />;
      $[6] = document;
      $[7] = ref;
      $[8] = t3;
      $[9] = t4;
      $[10] = uploads[0];
      $[11] = vault;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    return t5;
  }
  const t2 = uploads[0];
  let t3;
  if ($[13] !== uploads) {
    t3 = getTimestampRange(uploads);
    $[13] = uploads;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== t || $[16] !== uploads.length) {
    t4 = t(_temp, {
      count: uploads.length
    });
    $[15] = t;
    $[16] = uploads.length;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== t4) {
    t5 = <p className="text-label-2">{t4}</p>;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="text-label-2">⋅</span>;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setIsCollapsed(_temp2);
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== isCollapsed || $[23] !== t) {
    t8 = isCollapsed ? t(_temp3) : t(_temp4);
    $[22] = isCollapsed;
    $[23] = t;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t8) {
    t9 = <LinkButton onClick={t7}>{t8}</LinkButton>;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t5 || $[28] !== t9) {
    t10 = <div className="flex items-center gap-1">{t5}{t6}{t9}</div>;
    $[27] = t5;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t3 || $[32] !== uploads[0]) {
    t11 = <UploadTitleCard upload={t2} customTimestamp={t3} rightChildren={t10} />;
    $[30] = t10;
    $[31] = t3;
    $[32] = uploads[0];
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== document || $[35] !== isCollapsed || $[36] !== uploads || $[37] !== vault) {
    t12 = isCollapsed ? <div className="flex flex-col relative w-full top-3">{uploads.map((upload, index) => <UploadImageItem key={getUploadKey(upload)} document={document} upload={upload as DocumentUpload & {
        isLatest: boolean;
      }} vault={vault} rotateIndex={index} imageOnly={true} />)}</div> : <div className="grid w-full pl-4">{uploads.map((upload_0, index_0) => <LinedUploadContainer key={getUploadKey(upload_0)} isFirst={index_0 === 0} isLast={index_0 === uploads.length - 1}><UploadImageItem document={document} upload={upload_0} vault={vault} imageOnly={true} /></LinedUploadContainer>)}</div>;
    $[34] = document;
    $[35] = isCollapsed;
    $[36] = uploads;
    $[37] = vault;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== ref || $[40] !== t11 || $[41] !== t12) {
    t13 = <div className="flex flex-col items-center w-full" ref={ref}>{t11}{t12}</div>;
    $[39] = ref;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  return t13;
});
const LinedUploadContainer = t0 => {
  const $ = _c(7);
  const {
    children,
    isFirst,
    isLast
  } = t0;
  const t1 = !isLast;
  let t2;
  if ($[0] !== isFirst || $[1] !== isLast || $[2] !== t1) {
    t2 = cx("relative pl-4 pt-6\n      before:content-[''] before:absolute before:left-0 before:top-0 before:w-[1px] before:bg-[#cecece]\n      after:content-[''] after:absolute after:left-0 after:w-4 after:h-4\n      after:border-l after:border-b after:border-solid after:border-primary\n      after:rounded-bl-xl", {
      "pt-2": isFirst,
      "before:h-[55%] after:top-[55%]": isLast,
      "before:h-full after:top-[50%]": t1
    });
    $[0] = isFirst;
    $[1] = isLast;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== t2) {
    t3 = <div className={t2}>{children}</div>;
    $[4] = children;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export default FailedUploads;
function _temp($) {
  return $.titleCard.attempts;
}
function _temp2(prev) {
  return !prev;
}
function _temp3($_0) {
  return $_0.titleCard.showAll;
}
function _temp4($_1) {
  return $_1.titleCard.collapse;
}